// What the command bar finds inside the boards, on every board: doc pages, mind map ideas, calendar posts, pipeline
// deals, Calendar board events, a sheet's cells and the tasks in to-do lists, by their name or the words inside them,
// and the widgets on the other boards. Each opens where it lives (David, 15 Oct: "from any board, type a few letters and
// get to it"). What was typed matches the start of a word, never the middle of one (Fixes 12: "rent" found "Current").

import { createElement, type ReactNode } from "react";
import { CalendarDays, CheckSquare, FileText, Handshake, Lightbulb, Table2, WandSparkles } from "lucide-react";
import { pageTitle } from "../../shared/entries";
import { recordName } from "../../shared/records";
import { readTasks, SPEC } from "../../shared/catalog";
import { colName, parseAddr } from "../../shared/sheet/addr";
import type { Entry, ID, WidgetItem } from "../../shared/types";
import { switchBoard } from "../state/actions";
import { get, set } from "../state/store";
import { camera } from "../canvas/camera";
import { itemRect } from "../canvas/geometry";
import { openPage } from "../boards/doc/state";
import { openPost, setAt } from "../boards/calendar/state";
import { selectIdea } from "../boards/mindmap/state";
import { openDeal } from "../boards/pipeline/state";
import { cellSel, goToCells } from "../boards/sheet/state";
import { openEvent } from "../boards/agenda/state";
import { goTo } from "../state/goto";
import { WidgetIcon } from "./icons";

export interface Found {
  id: string;
  label: string;
  hint: string;
  icon: ReactNode;
  run: () => void;
}

const KIND_WORD: Record<string, string> = { page: "Page", node: "Idea", post: "Post", deal: "Deal", event: "Event", record: "Record" };
const ICON: Record<string, typeof FileText> = { page: FileText, node: Lightbulb, post: CalendarDays, task: CheckSquare, deal: Handshake, event: CalendarDays, cells: Table2, record: WandSparkles };

const esc = (q: string) => q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
let kept: { q: string; re: RegExp } | null = null;
/**
 * Whether what was typed starts a word in these words: "sam" in "Call with Sam", "rent" never in "Current". Cheap on a
 * big sheet's every cell (review): the words are looked through for it first, and the test is made once for each query.
 */
export function startsWord(words: string, q: string): boolean {
  if (!words.toLowerCase().includes(q.toLowerCase())) return false;
  if (kept?.q !== q) kept = { q, re: new RegExp(`(?:^|[^\\p{L}\\p{N}])${esc(q)}`, "iu") };
  return kept.re.test(words);
}

/** The words in a page's body, from its editor document, up to a few thousand characters. */
function bodyText(body: unknown, out: string[] = [], room = { left: 4000 }): string {
  if (room.left <= 0 || !body || typeof body !== "object") return out.join(" ");
  const n = body as { text?: unknown; content?: unknown };
  if (typeof n.text === "string") {
    out.push(n.text);
    room.left -= n.text.length;
  }
  if (Array.isArray(n.content)) for (const c of n.content) bodyText(c, out, room);
  return out.join(" ");
}

/** The text in a record's fields, a list's or an object's inside the field it sits in. */
const leaves = (v: unknown, depth = 0): string[] => (typeof v === "string" ? [v] : depth < 4 && v && typeof v === "object" ? Object.values(v).flatMap((x) => leaves(x, depth + 1)) : []);

/** An entry's name, and the words that also find it. */
function wordsOf(e: Entry): { name: string; more: string } | null {
  const d = e.data as Record<string, unknown>;
  const s = (k: string) => (typeof d[k] === "string" ? (d[k] as string) : "");
  switch (e.t) {
    case "page":
      return { name: pageTitle(e), more: bodyText(d.body) };
    case "node":
      return { name: s("text") || "Untitled idea", more: "" };
    case "post":
      return { name: s("title") || "Untitled post", more: Array.isArray(d.parts) ? (d.parts as unknown[]).filter((p) => typeof p === "string").join(" ") : "" };
    case "deal":
      return { name: s("company") || s("contact") || "Deal", more: `${s("contact")} ${s("email")}` };
    case "event":
      return { name: s("title") || "Untitled event", more: s("notes") };
    // a Custom board's record: its first text field is its name, every text field finds it (Fixes 13)
    case "record":
      return { name: recordName(e, 90), more: leaves(d).join(" ") };
  }
  return null;
}

/** Opens an entry where it lives: its board, then the page, idea, post, day or deal. */
function openEntry(e: Entry) {
  if (get().boardId !== e.boardId) switchBoard(e.boardId);
  const d = e.data as Record<string, unknown>;
  if (e.t === "page") openPage(e.boardId, e.id);
  else if (e.t === "node") selectIdea(e.boardId, e.id);
  else if (e.t === "post") {
    if (typeof d.day === "string") setAt(e.boardId, d.day);
    openPost(e.boardId, e.id);
  } else if (e.t === "deal") openDeal(e.id);
  else if (e.t === "event") {
    // its day on the Calendar board, then its card beside it, once the board has drawn it
    goTo({ board: e.boardId, entry: e.id });
    const card = (tries: number) => {
      const el = document.querySelector(`[data-ag-ev="${CSS.escape(e.id)}"]`);
      if (el || !tries) openEvent(e.id, el);
      else setTimeout(() => card(tries - 1), 120);
    };
    setTimeout(() => card(20), 60);
  }
}

/**
 * More that the command bar finds, from a board whose content is not on the board itself: a Files board adds the
 * notes in the folders it shows, while it is open. Each gives its finds with a rank, 0 best, as below.
 */
const finders = new Set<(q: string) => { f: Found; rank: number }[]>();
export function addFinder(fn: (q: string) => { f: Found; rank: number }[]): () => void {
  finders.add(fn);
  return () => void finders.delete(fn);
}

/** The best few things on the boards for what was typed: a name that starts with it first, then a name with a word that does, then the words inside. */
export function findItems(query: string, max = 6): Found[] {
  // a quoted phrase: what is typed is always matched as the words next to each other, so the quotes only come off
  const q = query.trim().replace(/^["“”](.*)["“”]$/, "$1").trim().toLowerCase();
  if (q.length < 2) return [];
  const s = get();
  const boards = new Map(s.boards.map((b) => [b.id, b.name] as [ID, string]));
  const hits: { f: Found; rank: number }[] = [];
  const rankOf = (name: string, more = "") => (name.toLowerCase().startsWith(q) ? 0 : startsWord(name, q) ? 1 : more && startsWord(more, q) ? 2 : -1);
  let cells = 0;
  for (const e of Object.values(s.entries)) {
    if (!boards.has(e.boardId)) continue;
    // a sheet's cells, each by what it holds as typed (a formula is not words), a few at most
    if (e.t === "cells") {
      const tabId = e.parent;
      if (cells >= 20 || !tabId) continue;
      const tab = s.entries[tabId];
      for (const [a, raw] of Object.entries((e.data.cells ?? {}) as Record<string, unknown>)) {
        if (typeof raw !== "string" || raw.startsWith("=")) continue;
        const v = raw.startsWith("'") ? raw.slice(1) : raw;
        const rank = rankOf(v);
        const at = rank < 0 ? null : parseAddr(a);
        if (!at) continue;
        hits.push({ rank, f: { id: `item:${e.id}:${a}`, label: v.replace(/\s+/g, " ").slice(0, 90), hint: `Cell ${colName(at.c)}${at.r + 1} on ${boards.get(e.boardId)}${typeof tab?.data.name === "string" ? `, ${tab.data.name}` : ""}`, icon: createElement(Table2, { size: 15 }), run: () => (get().boardId !== e.boardId && switchBoard(e.boardId), goToCells(e.boardId, tabId, cellSel(at.r, at.c))) } });
        if (++cells >= 20) break;
      }
      continue;
    }
    const w = wordsOf(e);
    if (!w) continue;
    const rank = rankOf(w.name, w.more);
    if (rank < 0) continue;
    hits.push({ rank, f: { id: "item:" + e.id, label: w.name.slice(0, 90), hint: `${KIND_WORD[e.t]} on ${boards.get(e.boardId)}${rank === 2 ? ", in its words" : ""}`, icon: createElement(ICON[e.t] ?? FileText, { size: 15 }), run: () => openEntry(e) } });
  }
  // the tasks in to-do lists, on every board
  for (const it of Object.values(s.items)) {
    if (it.kind !== "widget" || (it as WidgetItem).type !== "tasks" || !boards.has(it.boardId)) continue;
    const w = it as WidgetItem;
    for (const t of readTasks(w.props?.tasks)) {
      const rank = rankOf(t.text);
      if (rank < 0) continue;
      hits.push({ rank, f: { id: `item:${w.id}:${t.id}`, label: t.text.slice(0, 90), hint: `Task in ${w.title || "To-do"} on ${boards.get(w.boardId)}`, icon: createElement(CheckSquare, { size: 15 }), run: () => goTo({ board: w.boardId, item: w.id, task: t.id }) } });
    }
  }
  // widgets on the other boards (the ones on this board are in the bar already)
  for (const it of Object.values(s.items)) {
    if (it.kind !== "widget" || it.boardId === s.boardId || !boards.has(it.boardId)) continue;
    const w = it as WidgetItem;
    const rank = rankOf(w.title || SPEC[w.type]?.label || "");
    if (rank < 0) continue;
    hits.push({
      rank,
      f: {
        id: "item:" + w.id,
        label: w.title || SPEC[w.type]?.label || "Widget",
        hint: `Widget on ${boards.get(w.boardId)}`,
        icon: createElement(WidgetIcon, { type: w.type, size: 15 }),
        run: () => {
          switchBoard(w.boardId);
          // once the board is drawn, the widget picked and in view
          requestAnimationFrame(() => {
            const now = get().items[w.id];
            if (!now) return;
            set({ selection: [w.id] });
            camera.fit(itemRect(now), { maxZ: 1, pad: 120 });
          });
        },
      },
    });
  }
  for (const find of finders)
    try {
      hits.push(...find(q));
    } catch {
      /* a board's own finds never stop the bar */
    }
  return hits
    .sort((a, b) => a.rank - b.rank || a.f.label.length - b.f.label.length)
    .slice(0, max)
    .map((h) => h.f);
}
